import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Wallet, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useCart } from '@/contexts/CartContext';
import { toast } from 'sonner';

interface AddCreditsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const formatMask = (value: string): string => {
  const digits = value.replace(/\D/g, '');
  if (!digits) return '';
  const numeric = parseInt(digits, 10) / 100;
  return numeric.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const parseMask = (value: string): number => {
  if (!value) return 0;
  return parseFloat(value.replace(/\./g, '').replace(',', '.')) || 0;
};

export function AddCreditsDialog({ open, onOpenChange }: AddCreditsDialogProps) {
  const { addCredit } = useCart();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [masked, setMasked] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const parsed = parseMask(masked);
  const isValid = Number.isFinite(parsed) && parsed > 0;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMasked(formatMask(e.target.value));
  };

  const handleSubmit = async () => {
    if (!isValid) {
      toast.error(t('store.addCreditsDialog.invalid_value'));
      return;
    }
    setSubmitting(true);
    try {
      await addCredit(parsed);
      onOpenChange(false);
      setMasked('');
      navigate('/carrinho');
    } catch {
      // toast already shown by context
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenChange = (v: boolean) => {
    if (!v) setMasked('');
    onOpenChange(v);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wallet className="w-5 h-5 text-primary" />
            {t('store.addCreditsDialog.title')}
          </DialogTitle>
          <DialogDescription>
            {t('store.addCreditsDialog.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="space-y-2">
            <Label htmlFor="credit-amount">{t('store.addCreditsDialog.value_label')}</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm select-none">
                R$
              </span>
              <Input
                id="credit-amount"
                type="text"
                inputMode="numeric"
                placeholder={t('store.addCreditsDialog.placeholder')}
                value={masked}
                onChange={handleChange}
                className="pl-9"
                autoFocus
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => handleOpenChange(false)} disabled={submitting}>
            {t('store.addCreditsDialog.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={!isValid || submitting}>
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {t('store.addCreditsDialog.adding')}
              </>
            ) : (
              t('store.addCreditsDialog.confirm')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
